/**
 * GigaContextGraphBannerVertical — the MOBILE (portrait, 3:4) version of the
 * living context-graph banner. Five real record cards (Linear, Stripe,
 * Granola, Slack, Shopify) zigzag down the frame while ghost chips with real
 * brand logos drift at varied depths, tucked partially behind the cards.
 * Wires flex between nodes as everything idles, and micro-events tick values
 * every ~9s. CSS scoped under .gbv — safe next to the 16:9 GigaContextGraphBanner.
 * Usage: <GigaContextGraphBannerVertical />  (3:4, max-width 520px)
 */
import { useEffect, useRef } from "react";
const CSS = `
  .gbv{
    --bg:#2B211A;
    --ink:#2B211A;
    --card:#FFFFFF;
    --card-tile:#F4EFE7;
    --card-divider:rgba(43,33,26,.08);
    --muted:rgba(43,33,26,.55);
    --label:rgba(43,33,26,.45);
    --accent:#EA580C;
    --line:rgba(250,246,240,.17);
    --ghost:#FFFFFF;
    --ghost-bar:#E4DFD6;
  }
  .gbv, .gbv *{box-sizing:border-box;margin:0;padding:0}
  .gbv{
    position:relative;
    width:100%;
    max-width:520px;
    aspect-ratio:3/4;
    margin:0 auto;
    background:radial-gradient(rgba(244,240,234,0.07) 0.8px, transparent 0.8px) 0 0/13px 13px, var(--bg);
    overflow:hidden;
    border-radius:16px;
  }
  .gbv #wires{position:absolute;inset:0;width:100%;height:100%;z-index:1;pointer-events:none}
  .gbv #wires path.base{fill:none;stroke:var(--line);stroke-width:1;opacity:.75}
  .gbv .node{position:absolute;z-index:2;transform:translate(-50%,-50%);}
  .gbv .settled{opacity:1}
  .gbv .enter{opacity:0}
  .gbv .evt{display:inline-block;animation:evtpop .7s cubic-bezier(.3,1.5,.5,1)}
  @keyframes evtpop{0%{transform:scale(1);filter:brightness(1)}35%{transform:scale(1.16);filter:brightness(1.55)}100%{transform:scale(1);filter:brightness(1)}}
  @media (prefers-reduced-motion: reduce){
    .gbv .evt{animation:none}
  }
  .gbv .card{
    background:var(--card);
    border-radius:11px;
    padding:8px 10px 7px;
    width:160px;
    box-shadow:0 22px 44px -16px rgba(0,0,0,.5), 0 2px 8px rgba(0,0,0,.25);
    color:var(--ink);
    z-index:3;
  }
  .gbv .card .head{display:flex;align-items:center;gap:7px}
  .gbv .card .tile{
    width:25px;height:25px;flex:0 0 25px;border-radius:7px;
    background:var(--card-tile);
    display:flex;align-items:center;justify-content:center;
    position:relative;
  }
  .gbv .card .titles{flex:1 1 auto;min-width:0}
  .gbv .card .title{font:700 10px/1.25 "DM Sans",Inter,sans-serif;letter-spacing:-.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .gbv .card .sub{font:400 8.5px/1.3 Inter,sans-serif;color:var(--muted);margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .gbv .card .divider{height:1px;background:var(--card-divider);margin:7px 0 5px}
  .gbv .card .row{display:flex;align-items:center;gap:5px;padding:2.5px 0;font-size:8.5px}
  .gbv .card .row svg{width:9px;height:9px;flex:0 0 9px;opacity:.45}
  .gbv .card .row .l{color:var(--label);width:48px;flex:0 0 48px}
  .gbv .card .row .v{color:var(--ink);font-weight:600;white-space:nowrap}
  .gbv .card .row .v.link{color:var(--accent);animation:glint 6s ease-in-out infinite}
  .gbv .card .row .v.link .arr{display:inline-block;transform:translateY(-1px)}
  @keyframes glint{0%,86%,100%{filter:brightness(1)}92%{filter:brightness(1.45)}}
  .gbv .badge{
    margin-left:auto;flex:0 0 auto;font:600 7.5px/1 Inter;padding:3.5px 5.5px;border-radius:999px;
    background:rgba(22,163,74,.12);color:#16A34A;display:flex;align-items:center;gap:3.5px;
  }
  .gbv .badge i{width:4px;height:4px;border-radius:50%;background:#16A34A;display:block;animation:blink 3.5s ease-in-out infinite}
  @keyframes blink{0%,100%{opacity:1}50%{opacity:.35}}
  .gbv .ripple::before{
    content:"";position:absolute;inset:0;border-radius:7px;
    border:1.5px solid #A3C217;opacity:0;
    animation:ripple 7s ease-out 2s infinite;
  }
  @keyframes ripple{
    0%{opacity:0;transform:scale(1)}
    4%{opacity:.9}
    18%{opacity:0;transform:scale(1.9)}
    100%{opacity:0;transform:scale(1.9)}
  }
  .gbv .ghost{
    background:var(--ghost);
    border-radius:9px;
    padding:6px 8px;
    display:flex;align-items:center;gap:6px;
    box-shadow:0 12px 26px -12px rgba(0,0,0,.45), 0 1px 3px rgba(0,0,0,.2);
  }
  .gbv .ghost .g-icon{width:15px;height:15px;flex:0 0 15px;display:flex;align-items:center;justify-content:center}
  .gbv .ghost .bars{display:flex;flex-direction:column;gap:3.5px}
  .gbv .ghost .bars i{display:block;height:4px;border-radius:2.5px;background:var(--ghost-bar)}
  .gbv .ghost .bars i:nth-child(1){width:50px}
  .gbv .ghost .bars i:nth-child(2){width:32px}
  .gbv .ghost.sm .bars i:nth-child(1){width:30px}
  .gbv .ghost.sm .bars i:nth-child(2){display:none}
  .gbv .chip{
    background:var(--ghost);border-radius:8px;width:30px;height:30px;
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 12px 26px -12px rgba(0,0,0,.5), 0 1px 3px rgba(0,0,0,.2);
  }
  .gbv .chip.dark{background:#1A120C}
  .gbv .chip.pastel{background:#FBF9F5}
  /* Uniform logo scale — favicons are full-bleed squares (no internal
     padding like the old SVGs), so they render one size everywhere and get a
     hair of rounding to sit consistently in every container. */
  .gbv svg.logo, .gbv img.logo{width:13px;height:13px;display:block;object-fit:contain}
  .gbv img.logo{border-radius:2.5px}
  .gbv .ghost svg.logo, .gbv .ghost img.logo{width:12px;height:12px}
  .gbv .chip svg.logo, .gbv .chip img.logo{width:14px;height:14px}
`;
const MARKUP = `<svg id="wires"></svg>

  <div class="node card float enter" id="n-linear" style="left:32%;top:13.5%;--fd:8s;--edelay:.1s">
    <div class="head">
      <div class="tile" style="background:#ECEBFA">
        <img class="logo" src="https://www.google.com/s2/favicons?domain=linear.app&amp;sz=64" alt="Linear" loading="lazy"/>
      </div>
      <div class="titles">
        <div class="title">Fix onboarding flow</div>
        <div class="sub">ENG-462 · In Progress</div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><rect x="2" y="3" width="12" height="11" rx="2"/><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3"/></svg><span class="l">Status</span><span class="v">In Progress</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M2.8 14c.7-3 2.8-4.5 5.2-4.5S12.5 11 13.2 14"/></svg><span class="l">Assignee</span><span class="v">Naveen S.</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><rect x="2" y="3" width="12" height="11" rx="2"/><path d="M2 6.5h12"/></svg><span class="l">Due</span><span class="v">Next Friday</span></div>
  </div>

  <div class="node card float enter" id="n-stripe" style="left:66%;top:30.5%;width:187px;z-index:4;--fd:8.5s;--fdelay:2s;--edelay:.55s">
    <div class="head">
      <div class="tile" style="background:#EFEDFC">
        <img class="logo" src="https://www.google.com/s2/favicons?domain=stripe.com&amp;sz=64" alt="Stripe" loading="lazy"/>
      </div>
      <div class="titles">
        <div class="title">Growth Plan</div>
        <div class="sub">$299/mo · Renews Apr 30</div>
      </div>
      <div class="badge"><i></i>Active</div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><rect x="2.5" y="2.5" width="11" height="11" rx="2"/><path d="M5.5 6h5M5.5 9h3.5"/></svg><span class="l">Amount</span><span class="v">$299/mo</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><circle cx="8" cy="8" r="5.5"/><path d="M8 5v3l2 1.5"/></svg><span class="l">Customer</span><span class="v link">Acme Corp <span class="arr">↗</span></span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><rect x="3" y="6.5" width="10" height="7" rx="1.5"/><path d="M5.5 6.5v-2a2.5 2.5 0 0 1 5 0v2"/></svg><span class="l">Renews</span><span class="v">Apr 30, 2026</span></div>
  </div>

  <div class="node card float enter" id="n-granola" style="left:35%;top:50%;--fd:7s;--fdelay:1.6s;--edelay:.7s">
    <div class="head">
      <div class="tile ripple" style="background:#F2F5E2">
        <img class="logo" src="https://www.google.com/s2/favicons?domain=granola.ai&amp;sz=64" alt="Granola" loading="lazy"/>
      </div>
      <div class="titles">
        <div class="title">Investor Update — Notes</div>
        <div class="sub">Granola · synced 2 min ago</div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><path d="M4 2.5h8a1.5 1.5 0 0 1 1.5 1.5v9.5l-3-2h-6.5A1.5 1.5 0 0 1 2.5 10V4A1.5 1.5 0 0 1 4 2.5Z"/></svg><span class="l">Summary</span><span class="v" id="v-granola">4 action items</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M2.8 14c.7-3 2.8-4.5 5.2-4.5S12.5 11 13.2 14"/></svg><span class="l">With</span><span class="v">Alex Rivera</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><path d="M6.5 9.5 13 3M9 3h4v4"/><path d="M13 9.5V12a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 12V5a1.5 1.5 0 0 1 1.5-1.5H6"/></svg><span class="l">Assigned</span><span class="v link">in Linear <span class="arr">↗</span></span></div>
  </div>

  <div class="node card float enter" id="n-slack" style="left:65%;top:69.5%;width:163px;--fd:9s;--fdelay:.3s;--edelay:.85s">
    <div class="head">
      <div class="tile">
        <img class="logo" src="https://www.google.com/s2/favicons?domain=slack.com&amp;sz=64" alt="Slack" loading="lazy"/>
      </div>
      <div class="titles">
        <div class="title">#growth</div>
        <div class="sub" id="v-slack">3 new messages · just now</div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><path d="M5 2.5 4 13.5M12 2.5l-1 11M2.8 5.5h11M2.2 10.5h11"/></svg><span class="l">Channel</span><span class="v">#growth</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M2.8 14c.7-3 2.8-4.5 5.2-4.5S12.5 11 13.2 14"/></svg><span class="l">From</span><span class="v">Sarah Chen</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><rect x="2" y="3" width="12" height="11" rx="2"/><path d="M2 6.5h12"/></svg><span class="l">Date</span><span class="v">Today, 4:32 PM</span></div>
  </div>

  <div class="node card float enter" id="n-shopify" style="left:38%;top:85.5%;width:163px;--fd:7.5s;--fdelay:.6s;--edelay:.4s">
    <div class="head">
      <div class="tile" style="background:#EDF4E7">
        <img class="logo" src="https://www.google.com/s2/favicons?domain=shopify.com&amp;sz=64" alt="Shopify" loading="lazy"/>
      </div>
      <div class="titles">
        <div class="title">Order #2841</div>
        <div class="sub">12 minutes ago</div>
      </div>
      <div class="badge" id="b-paid"><i></i>Paid</div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><rect x="2.5" y="2.5" width="11" height="11" rx="2"/><path d="M5.5 6h5M5.5 9h3.5"/></svg><span class="l">Total</span><span class="v">$186.40</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M2.8 14c.7-3 2.8-4.5 5.2-4.5S12.5 11 13.2 14"/></svg><span class="l">Customer</span><span class="v">Maya R.</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#2B211A" stroke-width="1.4"><path d="M3 5.5 8 3l5 2.5v5L8 13l-5-2.5v-5Z"/></svg><span class="l">Items</span><span class="v">3</span></div>
  </div>`;
export function GigaContextGraphBannerVertical() {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const banner = rootRef.current;
    if (!banner) return;
    let stopped = false;
    let rafId = 0;
    let rto: ReturnType<typeof setTimeout> | undefined;
    if (!document.querySelector('link[data-giga-fonts]')) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      (l as HTMLElement).dataset.gigaFonts = "1";
      l.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@500;700;800&family=Inter:wght@400;500;600;700&display=swap";
      document.head.appendChild(l);
    }

    // REAL brand icons — every logo is the brand's actual favicon (the exact
    // multicolor icon people see in their browser tabs), served via Google's
    // favicon service. No flat single-color reinterpretations.
    const fav = (domain: string) => `<img class="logo" src="https://www.google.com/s2/favicons?domain=${domain}&amp;sz=64" alt="" loading="lazy"/>`;
    const LOGOS: Record<string, string> = {
      gmail: fav('mail.google.com'),
      gdrive: fav('drive.google.com'),
      notion: fav('notion.so'),
      linkedin: fav('linkedin.com'),
      hubspot: fav('hubspot.com'),
      figma: fav('figma.com'),
      github: fav('github.com'),
      zoom: fav('zoom.us'),
      salesforce: fav('salesforce.com'),
      gcal: fav('calendar.google.com'),
      person: fav('intercom.com'),
      building: fav('airtable.com'),
      envelope: fav('mailchimp.com'),
      lock: fav('okta.com'),
      calendar: fav('calendly.com')
    };
    LOGOS.sheets = fav('docs.google.com');
    LOGOS.mediumM = fav('asana.com');
    LOGOS.tilde = fav('loom.com');
    LOGOS.personpink = fav('canva.com');
    LOGOS.lockgreen = fav('quickbooks.intuit.com');
    LOGOS['gcal-chip'] = LOGOS.gcal;
    LOGOS['dropbox-chip'] = fav('dropbox.com');
    // Social + team staples (each used exactly once in the field).
    LOGOS.instagram = fav('instagram.com');
    LOGOS.facebook = fav('facebook.com');
    LOGOS.xtwitter = fav('x.com');
    LOGOS.youtube = fav('youtube.com');
    LOGOS.trello = fav('trello.com');

    // Scattered, layered placement: ghosts drift at varied x-positions and
    // several tuck partially BEHIND the hero cards (ghosts render at z-index
    // 2, cards at 3), so the field reads as depth-layered rather than stacked
    // columns. X is clamped so no node ever crosses the frame edge (ghost
    // pills stay within 16–84%, icon chips within 8–92%).
    const GHOSTS: [string, string, string, number, number, number, number][] = [['g1', 'ghost sm', 'gmail', 17, 7, 8, .2], ['g2', 'chip', 'gcal-chip', 66, 8, 9, 1.1], ['g3', 'ghost sm', 'person', 81, 11, 7, .6], ['g4', 'ghost', 'notion', 18, 20, 8.5, .9], ['g5', 'chip', 'lockgreen', 82, 21.5, 7.5, 1.4], ['g6', 'ghost sm', 'linkedin', 22, 28.5, 9, .4], ['g7', 'chip', 'figma', 90, 28, 8, 1.8], ['g8', 'ghost sm', 'gdrive', 17, 40, 7, 1.2], ['g9', 'chip', 'sheets', 86, 38, 8, .8], ['g10', 'chip pastel', 'calendar', 24, 45.5, 9, 2], ['g11', 'ghost sm', 'zoom', 82, 46.5, 7.5, .5], ['g12', 'ghost sm', 'tilde', 16, 55, 8, 1.5], ['g13', 'chip', 'dropbox-chip', 88, 56, 7, 2.2], ['g14', 'chip', 'mediumM', 14, 63.5, 8.5, .3], ['g15', 'ghost sm', 'salesforce', 82, 74, 7.5, 1.9], ['g16', 'ghost sm', 'personpink', 17, 73, 8, 1], ['g17', 'chip', 'instagram', 91, 64.5, 9, .7], ['g18', 'chip', 'facebook', 84, 84, 7, 1.3], ['g19', 'ghost sm', 'xtwitter', 19, 90, 8.5, 2.1], ['g20', 'ghost sm', 'youtube', 55, 93, 7.5, .2], ['g21', 'chip', 'trello', 88, 91.5, 8, 1.7], ['g22', 'chip pastel', 'hubspot', 47, 25, 8, 1.6], ['g23', 'ghost sm', 'envelope', 52, 59, 7.5, .9], ['g24', 'chip', 'github', 44, 79.5, 8.5, 1.2]];
    if (!banner.querySelector('[data-ghost]')) for (const [id, kind, logo, left, top, fd, fdelay] of GHOSTS) {
      const el = document.createElement('div');
      const isChip = kind.startsWith('chip');
      el.className = `node float enter ${isChip ? kind : 'ghost ' + kind.replace('ghost', '').trim()}`;
      if (!isChip) el.classList.add('ghost');
      el.id = id;
      el.dataset.ghost = '1';
      el.style.cssText = `left:${left}%;top:${top}%;--fd:${fd}s;--fdelay:${fdelay}s`;
      el.innerHTML = isChip ? `<span class="g-icon">${LOGOS[logo] || ''}</span>` : `<span class="g-icon">${LOGOS[logo] || ''}</span><span class="bars"><i></i><i></i></span>`;
      banner.appendChild(el);
    }

    // Safety net: if any CDN icon fails to load (some brands rotate out of
    // the Simple Icons set), swap to the brand's real favicon so a pill never
    // renders a broken-image placeholder.
    const FALLBACK_DOMAINS: Record<string, string> = {
      gmail: 'mail.google.com',
      googledrive: 'drive.google.com',
      notion: 'notion.so',
      hubspot: 'hubspot.com',
      zoom: 'zoom.us',
      salesforce: 'salesforce.com',
      googlecalendar: 'calendar.google.com',
      airtable: 'airtable.com',
      mailchimp: 'mailchimp.com',
      okta: 'okta.com',
      calendly: 'calendly.com',
      googlesheets: 'sheets.google.com',
      asana: 'asana.com',
      loom: 'loom.com',
      dropbox: 'dropbox.com',
      instagram: 'instagram.com',
      facebook: 'facebook.com',
      x: 'x.com',
      youtube: 'youtube.com',
      trello: 'trello.com'
    };
    banner.querySelectorAll('img.logo').forEach(img => {
      (img as HTMLImageElement).onerror = () => {
        const el = img as HTMLImageElement;
        el.onerror = null;
        const m = el.src.match(/simpleicons\.org\/([a-z0-9]+)/);
        const domain = m ? FALLBACK_DOMAINS[m[1]] : undefined;
        if (domain) el.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;else el.style.visibility = 'hidden';
      };
    });
    const LINKS: [string, string][] = [['n-stripe', 'n-linear'], ['n-stripe', 'n-granola'], ['n-granola', 'n-slack'], ['n-slack', 'n-shopify'], ['n-stripe', 'n-slack'], ['n-linear', 'n-granola'], ['n-linear', 'g1'], ['n-linear', 'g4'], ['n-linear', 'g2'], ['n-linear', 'g5'], ['n-stripe', 'g7'], ['n-stripe', 'g9'], ['n-stripe', 'g6'], ['n-stripe', 'g8'], ['n-granola', 'g10'], ['n-granola', 'g11'], ['n-granola', 'g13'], ['n-granola', 'g12'], ['n-slack', 'g14'], ['n-slack', 'g15'], ['n-slack', 'g17'], ['n-shopify', 'g16'], ['n-shopify', 'g18'], ['n-shopify', 'g19'], ['n-shopify', 'g20'], ['n-shopify', 'g21'], ['g3', 'n-linear'], ['g2', 'g3'], ['g22', 'n-stripe'], ['g23', 'n-granola'], ['g24', 'n-shopify']];
    const svg = banner.querySelector('#wires') as SVGSVGElement;
    const NS = 'http://www.w3.org/2000/svg';
    const timers: ReturnType<typeof setTimeout>[] = [];
    const after = (sec: number, fn: () => void) => timers.push(setTimeout(fn, sec * 1000));
    function setEntranceChoreography() {
      banner!.querySelectorAll('.node').forEach(el => {
        el.classList.remove('enter');
        el.classList.add('settled');
      });
    }
    function center(id: string): [number, number] {
      const el = banner!.querySelector('#' + id)!;
      const b = banner!.getBoundingClientRect(),
        r = el.getBoundingClientRect();
      return [r.left - b.left + r.width / 2, r.top - b.top + r.height / 2];
    }
    let wireRefs: {
      base: SVGPathElement;
      from: string;
      to: string;
      i: number;
    }[] = [];
    function drawWires() {
      svg.innerHTML = '';
      wireRefs = [];
      const b = banner!.getBoundingClientRect();
      svg.setAttribute('viewBox', `0 0 ${b.width} ${b.height}`);
      LINKS.forEach(([a, c], i) => {
        const [x1, y1] = center(a),
          [x2, y2] = center(c);
        const mx = (x1 + x2) / 2 + (i % 2 ? 28 : -28),
          my = (y1 + y2) / 2 + (i % 3 ? -22 : 22);
        const d = `M${x1},${y1} Q${mx},${my} ${x2},${y2}`;
        const base = document.createElementNS(NS, 'path') as SVGPathElement;
        base.setAttribute('d', d);
        base.setAttribute('class', 'base');
        svg.appendChild(base);
        wireRefs.push({
          base,
          from: a,
          to: c,
          i
        });
      });
    }
    const REDUCED = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    const DRIFT: Record<string, any> = {};
    let BASE: Record<string, [number, number]> = {};
    let idleOn = false;
    function measureBases() {
      BASE = {};
      const b = banner!.getBoundingClientRect();
      banner!.querySelectorAll('.node').forEach(el => {
        (el as HTMLElement).style.transform = 'translate(-50%,-50%)';
        const r = el.getBoundingClientRect();
        BASE[el.id] = [r.left - b.left + r.width / 2, r.top - b.top + r.height / 2];
      });
    }
    function initIdle() {
      banner!.querySelectorAll('.node').forEach(el => {
        if (REDUCED) return;
        const hero = el.classList.contains('card');
        DRIFT[el.id] = {
          ax: (hero ? 3.5 : 6) + Math.random() * (hero ? 2 : 3),
          ay: (hero ? 3 : 5) + Math.random() * (hero ? 2 : 3),
          wx: 2 * Math.PI / (4 + Math.random() * 3),
          wy: 2 * Math.PI / (3.2 + Math.random() * 3),
          px: Math.random() * Math.PI * 2,
          py: Math.random() * Math.PI * 2,
          ax2: hero ? 1.2 : 2.2,
          ay2: hero ? 1 : 1.8,
          wx2: 2 * Math.PI / (1.6 + Math.random() * 1.4),
          wy2: 2 * Math.PI / (1.4 + Math.random() * 1.4),
          px2: Math.random() * Math.PI * 2,
          py2: Math.random() * Math.PI * 2
        };
      });
      measureBases();
      idleOn = true;
    }
    function idleFrame(s: number) {
      const CUR: Record<string, [number, number]> = {};
      for (const id in DRIFT) {
        const d = DRIFT[id],
          b = BASE[id];
        if (!b) continue;
        const dx = d.ax * Math.sin(d.wx * s + d.px) + d.ax2 * Math.sin(d.wx2 * s + d.px2);
        const dy = d.ay * Math.sin(d.wy * s + d.py) + d.ay2 * Math.sin(d.wy2 * s + d.py2);
        CUR[id] = [b[0] + dx, b[1] + dy];
        const el = banner!.querySelector('#' + id) as HTMLElement | null;
        if (el) el.style.transform = `translate(calc(-50% + ${dx.toFixed(2)}px), calc(-50% + ${dy.toFixed(2)}px))`;
      }
      for (const w of wireRefs) {
        const A = CUR[w.from] || BASE[w.from],
          B = CUR[w.to] || BASE[w.to];
        if (!A || !B) continue;
        const mx = (A[0] + B[0]) / 2 + (w.i % 2 ? 28 : -28),
          my = (A[1] + B[1]) / 2 + (w.i % 3 ? -22 : 22);
        const d = `M${A[0].toFixed(1)},${A[1].toFixed(1)} Q${mx.toFixed(1)},${my.toFixed(1)} ${B[0].toFixed(1)},${B[1].toFixed(1)}`;
        w.base.setAttribute('d', d);
      }
    }
    let t0: number | null = null;
    function tick(ts: number) {
      if (stopped) return;
      if (t0 === null) t0 = ts;
      const s = (ts - t0) / 1000;
      if (!idleOn) initIdle();
      if (!REDUCED) idleFrame(s);
      rafId = requestAnimationFrame(tick);
    }
    function flashEl(el: Element | null) {
      if (!el) return;
      el.classList.remove('evt');
      void (el as HTMLElement).offsetWidth;
      el.classList.add('evt');
    }
    function tickValue(el: (Element & {
      __i?: number;
    }) | null, texts: string[]) {
      if (!el) return;
      flashEl(el);
      el.__i = 1 - (el.__i || 0);
      after(0.25, () => {
        el.textContent = texts[el.__i!];
      });
    }
    function microCycle() {
      after(0.5, () => flashEl(banner!.querySelector('#b-paid')));
      after(2.8, () => tickValue(banner!.querySelector('#v-granola'), ['4 action items', '5 action items']));
      after(5.4, () => tickValue(banner!.querySelector('#v-slack'), ['3 new messages \u00b7 just now', '4 new messages \u00b7 just now']));
    }
    function microLoop() {
      microCycle();
      timers.push(setTimeout(microLoop, 9000));
    }
    let started = false;
    const start = () => {
      if (stopped || started) return;
      started = true;
      setEntranceChoreography();
      drawWires();
      rafId = requestAnimationFrame(tick);
      after(1.2, microLoop);
    };
    const onResize = () => {
      clearTimeout(rto);
      rto = setTimeout(() => {
        drawWires();
        if (idleOn) measureBases();
      }, 120);
    };
    if (document.fonts && document.fonts.status !== "loaded") {
      document.fonts.ready.then(start);
      setTimeout(start, 1500);
    } else {
      start();
    }
    window.addEventListener("resize", onResize);
    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      clearTimeout(rto);
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", onResize);
    };
  }, []);
  return <>
      <style data-id="element-3936" data-mp="3gvs08s4q4iqg">{CSS}</style>
      <div ref={rootRef} className="gbv" dangerouslySetInnerHTML={{
      __html: MARKUP
    }} data-id="element-3937" data-mp="3gv9zbi749gc7" />
    </>;
}